import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { FormHelperText, Grid, TextField } from '@material-ui/core'
import { useFormContext } from 'react-hook-form'
import { useMutation } from 'react-query'
import { useDebouncedCallback } from 'use-debounce'

import { useJsApiLoader } from '~/hooks/useJsApiLoader'
import { getAutocompleteAddress } from '~/services/google/getAutocompleteAddress'

import AddressList, { Option } from './AddressList'

type InputAddressProps = {
  name: string
  label?: string
  disabled?: boolean
  icon: React.ElementType<React.SVGProps<SVGSVGElement>>
}

const InputAddress: React.FC<InputAddressProps> = ({
  name,
  label,
  disabled,
  icon: Icon,
}) => {
  const [inputValue, setInputValue] = useState<string>('')
  const [open, setOpen] = useState<boolean>(false)
  const { register, errors, setValue, watch, trigger } = useFormContext()

  const value = watch(name)

  const isError = !!errors[name]?.value
  const helperText = errors[name]?.value?.message

  const { isLoaded } = useJsApiLoader({
    apiKey: 'AIzaSyDr48zewIW7qbWbtqm2qxbnhXajBOfaugk',
  })

  const [mutate, { data, isLoading }] = useMutation(getAutocompleteAddress)

  const handleInputChange = useDebouncedCallback((value: string) => {
    setOpen(true)
    isLoaded && mutate(value)
  }, 300)

  const options = useMemo(() => {
    if (!data) {
      return []
    }

    return data.map((it) => ({
      name: it.description,
      value: it.place_id,
    }))
  }, [data])

  const handleSelectOption = useCallback(
    (option: Option) => {
      setValue(name, option, { shouldValidate: true })
      setOpen(false)
    },
    [setValue, name],
  )

  const handleBlur = useCallback(
    (e) => {
      trigger(name)
      if (e.relatedTarget && e.relatedTarget.tabIndex !== -1) {
        setOpen(false)
        if (value) {
          setInputValue(value.name)
        }
      }

      if (!e.target.value.trim()) {
        setInputValue('')
        setValue(name, { name: '', value: '' }, { shouldValidate: true })
      }
    },
    [value, name, trigger, setValue],
  )

  const handleChange = useCallback(
    (event) => {
      setInputValue(event.target.value)
      handleInputChange.callback(event.target.value)
    },
    [handleInputChange],
  )

  useEffect(() => {
    register(name)
  }, [name, register])

  useEffect(() => {
    setInputValue(value?.name || '')
  }, [value])

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={1} sm={2} md={1} justify="center">
        <Icon />
      </Grid>
      <Grid item xs={11} sm={10} md={11}>
        <div style={{ position: 'relative' }}>
          <TextField
            label={label}
            name={name}
            variant="outlined"
            error={isError}
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            autoComplete="new-password"
            disabled={disabled}
            fullWidth
          />
          <AddressList
            open={open}
            loading={isLoading}
            options={options}
            onSelect={handleSelectOption}
          />
        </div>
        {helperText && (
          <FormHelperText error={isError}>{helperText}</FormHelperText>
        )}
      </Grid>
    </Grid>
  )
}

export default InputAddress
